import React, { useContext, useEffect, useState } from 'react';
import { CreateEditCategoryModalView, CreateEditCategoryModalViewProps } from './CreateEditCategoryModal.view';
import { RegisterOptions, useForm, UseFormReturn } from 'react-hook-form';
import { FormOptions } from '../../../../constants/form';
import { ApolloError, useApolloClient } from '@apollo/client';
import {
  CategoryColor,
  CreateCategoryInput,
  CreateCategoryMutationFn,
  GetCategoriesQuery,
  GetCategoriesQueryVariables,
  GetSocialProfilesLazyQueryHookResult,
  UpdateCategoryMutationFn,
  GetCategoriesDocument,
  GetQueueMetadataDocument,
  UsageFieldsFragmentDoc,
  PlanUsageType
} from '../../../../models/graphql';
import { useSelectedOrganization } from '../../../../hooks/useSelectedOrganization';
import { ToggleAction } from '../../../../components/forms/Toggle/Toggle.controller';
import { getApolloErrorMessage, remapApolloErrors } from '../../../../helpers/remapApolloErrors';
import { showToast } from '../../../../helpers/showToast';
import { useLocation, useNavigate } from 'react-router-dom';
import { ValidationMessages } from '../../../../constants/validation-messages';
import { useDidMount } from 'hooks/useDidMount';
import { format } from 'date-fns';
import { AppContext } from 'providers/AppProvider';

export type CreateEditCategoryModalControllerProps = Omit<
  CreateEditCategoryModalViewProps,
  | 'socialProfiles'
  | 'onSubmit'
  | 'submitted'
  | 'useFormInstance'
  | 'nameRules'
  | 'toggleRules'
  | 'colorPickerRules'
  | 'calendarRules'
  | 'profileRules'
  | 'showProfileSelector'
  | 'toggleProfileHandler'
  | 'showCalendarSelector'
  | 'toggleCalendarHandler'
  | 'checkAllProfiles'
  | 'setCheckAllProfiles'
  | 'calendarStartDate'
  | 'calendarEndDate'
  | 'isLoading'
> & {
  createCategory: CreateCategoryMutationFn;
  updateCategory: UpdateCategoryMutationFn;
  getProfiles: GetSocialProfilesLazyQueryHookResult[0];
  profilesResults: GetSocialProfilesLazyQueryHookResult[1];
};

const formatInputDate = (stringDate: string) => {
  const date = new Date(stringDate);
  return format(date, '--MM-dd');
};

export const CreateEditCategoryModalController = (props: CreateEditCategoryModalControllerProps): JSX.Element => {
  const apolloClient = useApolloClient();
  const { setLimitReachedModalState } = useContext(AppContext);
  const cache = apolloClient.cache;
  const { selectedWorkspace } = useSelectedOrganization();
  const navigate = useNavigate();
  const location = useLocation();
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [checkAllProfiles, setCheckAllProfiles] = useState<boolean>(true);
  const [showProfileSelector, setShowProfileSelector] = useState<boolean>(
    (props.category?.defaultSocialProfiles && props.category.defaultSocialProfiles.length > 0) || false
  );
  const [showCalendarSelector, setShowCalendarSelector] = useState<boolean>(props.category?.seasonal || false);
  const socialProfiles = props.profilesResults.data?.workspace.socialProfiles || [];
  const defaultFormValues: Record<string, string | boolean | Date> = {
    id: props.category?.id || '',
    name: props.category?.name || '',
    color: props.category?.color || CategoryColor.LIGHT_GREY,
    seasonal: props.category?.seasonal || false
  };
  const isLoading = props.profilesResults.loading;

  let calendarStartDate;
  let calendarEndDate;
  if (props.category?.seasonalStartDate && props.category.seasonalEndDate) {
    const startDate = props.category.seasonalStartDate.replace('--', '').split('-');
    const endDate = props.category.seasonalEndDate.replace('--', '').split('-');

    const startYear = new Date().getFullYear();
    const endYear = endDate < startDate ? new Date().getFullYear() + 1 : startYear;

    calendarStartDate = new Date(startYear, Number(startDate[0]) - 1, Number(startDate[1]));
    calendarEndDate = new Date(endYear, Number(endDate[0]) - 1, Number(endDate[1]));

    defaultFormValues['seasonalStartDate'] = calendarStartDate;
    defaultFormValues['seasonalEndDate'] = calendarEndDate;
  }

  const useFormInstance: UseFormReturn = useForm({
    ...FormOptions,
    defaultValues: defaultFormValues
  });

  if (!isLoading && props.category) {
    props.category.defaultSocialProfiles?.forEach(profile => {
      const profileIndex = socialProfiles.findIndex(x => x.id === profile?.id);
      if (profileIndex > -1 && profile) {
        const profileValue = useFormInstance.getValues(`profiles.${profileIndex}`);
        if (profileValue !== profile.id && profileValue !== false) {
          useFormInstance.setValue(`profiles.${profileIndex}`, profile.id);
        }
      }
    });
  }

  useDidMount(() => {
    selectedWorkspace &&
      props.getProfiles({
        variables: { workspaceId: selectedWorkspace.id }
      });
  });

  const toggleProfileHandler: ToggleAction = (newVal: boolean, complete: (keepNewVal: boolean) => void) => {
    setShowProfileSelector(!showProfileSelector);
    complete(true);
  };

  const toggleCalendarHandler: ToggleAction = (newVal: boolean, complete: (keepNewVal: boolean) => void) => {
    setShowCalendarSelector(!showCalendarSelector);
    complete(true);
  };

  const onSubmit = async (data: {
    name: string;
    color: CategoryColor;
    seasonal: boolean;
    profiles: number[];
    seasonalStartDate: string;
    seasonalEndDate: string;
  }) => {
    setSubmitted(true);

    if (selectedWorkspace?.id) {
      const input: CreateCategoryInput = {
        name: data.name,
        color: data.color,
        defaultSocialProfileIds: [],
        seasonalStartDate: '',
        seasonalEndDate: ''
      };

      if (typeof data.profiles === 'object') {
        input.defaultSocialProfileIds = data.profiles.flatMap((value, index) =>
          value && socialProfiles[index] ? socialProfiles[index].id : []
        );
      }

      // Note: If not toggled, we should pass empty strings to allow this to reflect in the backend
      if (showCalendarSelector) {
        input.seasonalStartDate = formatInputDate(data.seasonalStartDate);
        input.seasonalEndDate = formatInputDate(data.seasonalEndDate);
      }

      if (props.category) {
        props
          .updateCategory({
            variables: {
              categoryId: props.category.id,
              input: input
            },
            update: () => {
              props.onClose();
              apolloClient.refetchQueries({
                include: [GetQueueMetadataDocument]
              });
            }
          })
          .catch(e => {
            const errorForRemapping = e as ApolloError;
            const mappedError = remapApolloErrors(errorForRemapping);
            showToast({
              summary: 'Error in updating the category',
              detail: mappedError,
              severity: 'error'
            });
            setSubmitted(false);
          });
      } else {
        props
          .createCategory({
            variables: {
              workspaceId: selectedWorkspace.id,
              input: input
            },
            update: () => {
              props.onClose();
              apolloClient.refetchQueries({
                include: [GetQueueMetadataDocument]
              });
            }
          })
          .then(result => {
            if (!selectedWorkspace?.id || !result.data?.createCategory) {
              return;
            }

            const data = cache.readQuery<GetCategoriesQuery, GetCategoriesQueryVariables>({
              query: GetCategoriesDocument,
              variables: { workspaceId: selectedWorkspace.id }
            });

            if (!data || !data.workspace) {
              return;
            }

            const newData: GetCategoriesQuery = {
              workspace: {
                __typename: 'Workspace',
                id: data.workspace.id,
                categories: [
                  ...data.workspace.categories,
                  {
                    __typename: 'Category',
                    id: result.data.createCategory.id,
                    name: result.data.createCategory.name,
                    seasonal: result.data.createCategory.seasonal,
                    seasonalStartDate: result.data.createCategory.seasonalStartDate,
                    seasonalEndDate: result.data.createCategory.seasonalEndDate,
                    seasonallyPaused: result.data.createCategory.seasonallyPaused,
                    archived: result.data.createCategory.archived,
                    archivedDate: result.data.createCategory.archivedDate,
                    color: result.data.createCategory.color,
                    onboardingContent: result.data.createCategory.onboardingContent,
                    defaultSocialProfiles: result.data.createCategory.defaultSocialProfiles,
                    archivedAttachedPostCount: 0
                  }
                ]
              }
            };

            cache.writeQuery({
              query: GetCategoriesDocument,
              variables: { workspaceId: selectedWorkspace.id },
              data: newData
            });

            const oldUsage: PlanUsageType | null = cache.readFragment({
              fragment: UsageFieldsFragmentDoc,
              fragmentName: 'UsageFields',
              id: 'PlanUsageType:{}'
            });
            const newUsage = { ...oldUsage, activeCategories: (oldUsage?.activeCategories ?? 0) + 1 };
            cache.writeFragment({
              fragment: UsageFieldsFragmentDoc,
              fragmentName: 'UsageFields',
              id: 'PlanUsageType:{}',
              data: newUsage
            });

            // Redirect user to Active view on completion if they are currently on Archived page
            if (location.pathname === '/publish/categories/archived') {
              navigate({ pathname: '/publish/categories' });
            }
          })
          .catch(e => {
            const errorForRemapping = e as ApolloError;
            const mappedError = remapApolloErrors(errorForRemapping);
            if (getApolloErrorMessage(e) === 'PLAN_LIMIT_EXCEEDED') {
              setLimitReachedModalState({
                isOpen: true,
                type: 'category'
              });
            } else {
              showToast({
                summary: 'Error in creating the category',
                detail: mappedError,
                severity: 'error'
              });
            }

            setSubmitted(false);
          });
      }
    }
  };

  const nameRules: RegisterOptions = {
    required: ValidationMessages.categories.name.required,
    maxLength: {
      value: 50,
      message: ValidationMessages.categories.name.maxLength
    }
  };

  const toggleRules = {};
  const colorPickerRules: RegisterOptions = {
    required: ValidationMessages.colorPicker.required
  };
  const calendarRules: RegisterOptions = {
    required: ValidationMessages.calendar.required
  };
  const profileRules: RegisterOptions = {
    // No validation is required as no-profiles is valid
  };

  useEffect(() => {
    if (props.profilesResults.error) {
      // todo we need to handle this
    }
  });

  const viewProps: CreateEditCategoryModalViewProps = {
    ...props,
    socialProfiles,
    onSubmit,
    submitted,
    useFormInstance,
    nameRules,
    toggleRules,
    colorPickerRules,
    calendarRules,
    profileRules,
    showProfileSelector,
    toggleProfileHandler,
    showCalendarSelector,
    toggleCalendarHandler,
    checkAllProfiles,
    setCheckAllProfiles,
    calendarStartDate,
    calendarEndDate,
    isLoading
  };
  return <CreateEditCategoryModalView {...viewProps} />;
};
